import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Bookmark, ClipboardList, MessageSquare, FolderOpen, CreditCard, LogOut, UserCog,
  Building2, FileLock2, UserRound, Send, UserPlus, Megaphone, CalendarClock, Settings, Menu,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { isMember } from "@/lib/api";

export const BUSINESS_NAV = [
  { to: "/app", label: "Overview", short: "Home", icon: LayoutDashboard, end: true, tab: true },
  { to: "/app/candidates", label: "Candidates", icon: Users, tab: true },
  { to: "/app/jobs", label: "Job Ads", short: "Jobs", icon: Megaphone, tab: true },
  { to: "/app/shortlist", label: "Shortlist", icon: Bookmark },
  { to: "/app/requests", label: "Hires & Interviews", icon: ClipboardList },
  { to: "/app/messages", label: "Messages", icon: MessageSquare, tab: true },
  { to: "/app/resources", label: "Business Files", icon: FolderOpen },
  { to: "/app/membership", label: "Membership", icon: CreditCard },
  { to: "/app/business-profile", label: "Business Profile", icon: Building2 },
  { to: "/app/documents", label: "Documents", icon: FileLock2 },
  { to: "/app/account", label: "Account", icon: Settings },
];

export const CANDIDATE_NAV = [
  { to: "/candidate", label: "Overview", short: "Home", icon: LayoutDashboard, end: true, tab: true },
  { to: "/candidate/profile", label: "My Profile", icon: UserRound },
  { to: "/candidate/jobs", label: "Find Jobs", short: "Jobs", icon: Megaphone, tab: true },
  { to: "/candidate/availability", label: "Availability", icon: CalendarClock },
  { to: "/candidate/offers", label: "Offers", icon: Send, tab: true },
  { to: "/candidate/documents", label: "Documents", icon: FileLock2 },
  { to: "/candidate/messages", label: "Messages", icon: MessageSquare, tab: true },
  { to: "/candidate/account", label: "Account", icon: Settings },
];

export const ADMIN_NAV = [
  { to: "/admin", label: "Overview", short: "Home", icon: LayoutDashboard, end: true, tab: true },
  { to: "/admin/candidates", label: "Candidates", icon: Users, tab: true },
  { to: "/admin/jobs", label: "Job Ads", icon: Megaphone },
  { to: "/admin/requests", label: "Hires & Interviews", short: "Requests", icon: ClipboardList, tab: true },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, tab: true },
  { to: "/admin/resources", label: "Business Files", icon: FolderOpen },
  { to: "/admin/members", label: "Members", icon: UserCog },
  { to: "/admin/applications", label: "Applications", icon: UserPlus },
  { to: "/admin/documents", label: "Documents", icon: FileLock2 },
  { to: "/admin/account", label: "Account", icon: Settings },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const NavItem = ({ item, onClick, prefix = "nav" }) => (
  <NavLink
    to={item.to}
    end={item.end}
    onClick={onClick}
    data-testid={`${prefix}-${slug(item.label)}`}
    className={({ isActive }) =>
      `flex shrink-0 items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
        isActive ? "bg-slate-900 text-white" : "text-stone-600 hover:bg-stone-200/60 hover:text-stone-900"
      }`
    }
  >
    <item.icon className="h-4 w-4" strokeWidth={1.75} />
    <span className="whitespace-nowrap">{item.label}</span>
  </NavLink>
);

const tabCls = (active) => `flex flex-1 flex-col items-center gap-1 pt-2 pb-1.5 text-[10px] font-medium transition-colors duration-200 ${active ? "text-slate-900" : "text-stone-400"}`;

const BottomNav = ({ items, onMore }) => (
  <nav data-testid="bottom-nav" className="lg:hidden fixed inset-x-0 bottom-0 z-40 flex border-t border-stone-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl">
    {items.filter((i) => i.tab).map((i) => (
      <NavLink key={i.to} to={i.to} end={i.end} data-testid={`tab-${slug(i.label)}`} className={({ isActive }) => tabCls(isActive)}>
        {({ isActive }) => (<>
          <span className={`grid h-7 w-12 place-items-center rounded-full transition-colors duration-200 ${isActive ? "bg-amber-100" : ""}`}><i.icon className="h-5 w-5" strokeWidth={isActive ? 2.25 : 1.75} /></span>
          {i.short || i.label}
        </>)}
      </NavLink>
    ))}
    <button data-testid="tab-more" onClick={onMore} className={tabCls(false)}>
      <span className="grid h-7 w-12 place-items-center"><Menu className="h-5 w-5" strokeWidth={1.75} /></span>More
    </button>
  </nav>
);

export const DashboardLayout = ({ items, admin = false, candidate = false }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [more, setMore] = useState(false);
  const member = isMember(user);
  const home = admin ? "/admin" : candidate ? "/candidate" : "/app";
  const portal = admin ? "Admin console" : candidate ? "Candidate portal" : "Client portal";
  const doLogout = async () => { setMore(false); await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-[#FBFBF9] lg:flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-stone-200 bg-[#F5F5F0] px-4 py-6 sticky top-0 h-screen overflow-y-auto">
        <div className="px-2"><Logo to={home} /></div>
        <p className="mt-8 px-3 text-[11px] font-mono uppercase tracking-[0.18em] text-stone-500">{portal}</p>
        <nav className="mt-3 flex flex-col gap-1">{items.map((i) => <NavItem key={i.to} item={i} />)}</nav>
        <div className="mt-auto rounded-md border border-stone-200 bg-white p-4">
          <p className="text-sm font-medium text-stone-900 truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs text-stone-500 truncate">{user?.company || user?.email}</p>
          {!admin && !candidate && (
            <span data-testid="sidebar-membership-badge" className={`mt-3 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${member ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
              {member ? `${user?.membership?.plan} member` : "No active plan"}
            </span>
          )}
          <button onClick={doLogout} data-testid="logout-button" className="mt-3 flex w-full items-center gap-2 text-xs text-stone-500 hover:text-stone-900 transition-colors">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-40 border-b border-stone-200 bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <Logo to={home} />
          <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-500" data-testid="mobile-portal-label">{portal}</span>
        </div>
      </header>

      <main className="flex-1 min-w-0 px-4 pt-6 pb-[calc(env(safe-area-inset-bottom)+6rem)] sm:px-8 lg:px-12 lg:py-10">
        <Outlet />
      </main>

      <BottomNav items={items} onMore={() => setMore(true)} />
      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="bottom" data-testid="more-sheet" className="max-h-[85vh] overflow-y-auto rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-xl">{user?.name}</SheetTitle>
            <SheetDescription>{user?.company || user?.email}{!admin && !candidate ? ` · ${member ? `${user?.membership?.plan} member` : "No active plan"}` : ""}</SheetDescription>
          </SheetHeader>
          <nav className="mt-4 grid grid-cols-1 gap-1 sm:grid-cols-2">{items.map((i) => <NavItem key={i.to} item={i} prefix="more" onClick={() => setMore(false)} />)}</nav>
          <button onClick={doLogout} data-testid="mobile-logout-button" className="mt-4 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-red-600 hover:bg-red-50">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </SheetContent>
      </Sheet>
    </div>
  );
};
